import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  Layers,
  LineChart as LineChartIcon,
  ListChecks,
  MessageSquareText,
  Minus,
  RefreshCw,
  Sparkles,
  Star,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../../services/api';
import { feedbackHeroTokens } from './feedbackHeroTokens';
import './FeedbackHero.css';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const C = feedbackHeroTokens.colors;
// Período 1 = referência (tom frio/neutro); Período 2 = atual (verde da marca).
const P1_COLOR = '#94A3B8';
const P2_COLOR = C.pos.accent; // #2E8B57

const toInputDate = (date) => {
  const safe = date instanceof Date ? date : new Date();
  const year = safe.getFullYear();
  const month = String(safe.getMonth() + 1).padStart(2, '0');
  const day = String(safe.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseInputDate = (value) => {
  const [year, month, day] = String(value || '').split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const addDays = (date, days) => new Date(date.getTime() + days * MS_PER_DAY);

const periodLengthDays = (period) => {
  const from = startOfDay(parseInputDate(period.from));
  const to = startOfDay(parseInputDate(period.to));
  return Math.max(1, Math.round((to - from) / MS_PER_DAY) + 1);
};

// Janela de igual tamanho imediatamente anterior ao período informado.
const previousPeriodOf = (period) => {
  const length = periodLengthDays(period);
  const from = startOfDay(parseInputDate(period.from));
  const prevEnd = addDays(from, -1);
  const prevStart = addDays(prevEnd, -(length - 1));
  return { from: toInputDate(prevStart), to: toInputDate(prevEnd) };
};

const buildPresets = () => {
  const today = startOfDay(new Date());
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  return [
    {
      id: '7d',
      label: 'Últimos 7 dias',
      period: { from: toInputDate(addDays(today, -6)), to: toInputDate(today) },
    },
    {
      id: '30d',
      label: 'Últimos 30 dias',
      period: { from: toInputDate(addDays(today, -29)), to: toInputDate(today) },
    },
    {
      id: '90d',
      label: 'Últimos 90 dias',
      period: { from: toInputDate(addDays(today, -89)), to: toInputDate(today) },
    },
    {
      id: 'month',
      label: 'Este mês',
      period: { from: toInputDate(firstOfMonth), to: toInputDate(today) },
    },
    {
      id: 'lastMonth',
      label: 'Mês passado',
      period: { from: toInputDate(firstOfLastMonth), to: toInputDate(lastOfLastMonth) },
    },
  ];
};

const buildInitialPeriods = () => {
  const presets = buildPresets();
  const period2 = presets.find((preset) => preset.id === '30d').period;
  return {
    period2,
    period1: previousPeriodOf(period2),
  };
};

const formatNumber = (value, digits = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(numeric);
};

const formatPercent = (value, digits = 1) => `${formatNumber(value, digits)}%`;

const formatDateLabel = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date)
    : '-';

const formatRangeLabel = (period) =>
  `${formatDateLabel(parseInputDate(period.from))} a ${formatDateLabel(parseInputDate(period.to))}`;

const getSentimentTotal = (summary, sentiment) => {
  const found = (summary?.por_sentimento || []).find((item) => item.sentimento === sentiment);
  return Number(found?.total || 0);
};

const normalizeSummary = (summary = {}) => {
  const total = Number(summary.total_feedbacks || 0);
  const positivos = getSentimentTotal(summary, 'positivo');
  const neutros = getSentimentTotal(summary, 'neutro');
  const negativos = getSentimentTotal(summary, 'negativo');

  return {
    total,
    media: summary.media_nota == null ? null : Number(summary.media_nota),
    positivos,
    neutros,
    negativos,
    pendencias: Number(summary.pendencias_atencao || 0),
    taxaPositiva: total > 0 ? (positivos / total) * 100 : 0,
    taxaNegativa: total > 0 ? (negativos / total) * 100 : 0,
  };
};

const readFeedbackList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const buildPointMap = (points = []) => {
  const map = new Map();
  points.forEach((item) => {
    const category = String(item.categoria || '').trim();
    if (!category) return;
    map.set(category, {
      categoria: category,
      positivo: Number(item.positivo || 0),
      negativo: Number(item.negativo || 0),
    });
  });
  return map;
};

// Série diária alinhada pelo dia relativo (Dia 1..N) para permitir sobrepor
// dois períodos de tamanhos diferentes no mesmo gráfico.
const buildDailySeries = (feedbacks, period) => {
  const from = startOfDay(parseInputDate(period.from));
  const length = periodLengthDays(period);
  const days = Array.from({ length }, (_, index) => ({
    idx: index + 1,
    date: addDays(from, index),
    count: 0,
    rated: 0,
    sum: 0,
    neg: 0,
  }));

  feedbacks.forEach((fb) => {
    if (!fb.criado_em) return;
    const created = startOfDay(new Date(fb.criado_em));
    const offset = Math.round((created - from) / MS_PER_DAY);
    if (offset < 0 || offset >= length) return;
    const bucket = days[offset];
    bucket.count += 1;
    const nota = Number(fb.nota);
    if (Number.isFinite(nota) && nota > 0) {
      bucket.rated += 1;
      bucket.sum += nota;
      if (nota <= 2) bucket.neg += 1;
    }
  });

  return days.map((day) => ({
    ...day,
    avg: day.rated > 0 ? day.sum / day.rated : null,
  }));
};

const buildRatingDistribution = (feedbacks) => {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  feedbacks.forEach((fb) => {
    const nota = Math.round(Number(fb.nota));
    if (Number.isFinite(nota) && nota >= 1 && nota <= 5) counts[nota] += 1;
  });
  return counts;
};

const diffTone = (value, inverse = false) => {
  if (!value || value === 0) return { text: C.subtext, bg: '#F1F5F9', border: 'rgba(15,23,42,0.08)' };
  const isGood = inverse ? value < 0 : value > 0;
  return isGood
    ? { text: C.pos.fg, bg: C.pos.bg, border: 'rgba(46,139,87,0.25)' }
    : { text: C.neg.fg, bg: C.neg.bg, border: 'rgba(212,109,99,0.25)' };
};

const DeltaBadge = ({ value, inverse = false, suffix = '', decimals = 0 }) => {
  const tone = diffTone(value, inverse);
  const Icon = value === 0 ? Minus : value > 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-bold"
      style={{ color: tone.text, backgroundColor: tone.bg, borderColor: tone.border }}
    >
      <Icon size={13} className="shrink-0" />
      {value > 0 ? '+' : ''}
      {formatNumber(value, decimals)}
      {suffix}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, previous, diff, suffix = '', inverse = false, decimals = 0, hint }) => (
  <div className="rounded-xl border border-slate-200/70 bg-white p-4 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
    <div className="flex items-center gap-1.5 text-slate-500">
      <Icon size={16} className="shrink-0" />
      <span className="text-[11px] font-bold uppercase leading-tight tracking-wide">{label}</span>
    </div>
    <div className="mt-3 flex flex-wrap items-end justify-between gap-x-2 gap-y-1.5">
      <span className="text-3xl font-bold leading-none text-slate-900">
        {formatNumber(value, decimals)}
        {suffix}
      </span>
      <DeltaBadge value={diff} inverse={inverse} suffix={suffix} decimals={decimals} />
    </div>
    <div className="mt-2 text-[11px] font-semibold text-slate-500">
      Antes: {formatNumber(previous, decimals)}
      {suffix}
    </div>
    {hint && <div className="mt-1.5 text-[11px] leading-snug text-slate-400">{hint}</div>}
  </div>
);

const AutoCompareToggle = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="inline-flex max-w-full items-center gap-2.5 text-left text-xs font-semibold text-slate-600"
  >
    <span
      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
        checked ? 'bg-[#2E8B57]' : 'bg-slate-300'
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${
          checked ? 'left-[18px]' : 'left-0.5'
        }`}
      />
    </span>
    <span className="leading-snug">Escolher o período de "antes" automaticamente</span>
  </button>
);

const PeriodControlBar = ({
  presets,
  activePresetId,
  onPreset,
  autoCompare,
  onToggleAuto,
  period1,
  period2,
  onChangePeriod1,
  onChangePeriod2,
}) => (
  <div className="rounded-xl border border-slate-200/70 bg-white p-4 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
          <CalendarDays size={15} />
          Atalhos
        </span>
        {presets.map((preset) => {
          const active = preset.id === activePresetId;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onPreset(preset)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                active
                  ? 'border-[#2E8B57] bg-[#2E8B57] text-white shadow-sm'
                  : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-[#2E8B57]/40 hover:text-[#1F5C3A]'
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="shrink-0 lg:pl-4">
        <AutoCompareToggle checked={autoCompare} onChange={onToggleAuto} />
      </div>
    </div>

    <p className="mt-3 text-[11px] leading-snug text-slate-500">
      Escolha o período que quer analisar em <strong className="text-slate-600">Agora</strong>.
      Deixamos o <strong className="text-slate-600">Antes</strong> no período anterior do mesmo
      tamanho, para a comparação ser justa. Se preferir, ajuste as datas manualmente.
    </p>

    <div className="mt-4 grid gap-3 lg:grid-cols-2">
      {[
        { key: 'p1', title: 'Antes · comparação', period: period1, onChange: onChangePeriod1, dot: P1_COLOR },
        { key: 'p2', title: 'Agora · período analisado', period: period2, onChange: onChangePeriod2, dot: P2_COLOR },
      ].map(({ key, title, period, onChange, dot }) => (
        <div key={key} className="rounded-lg border border-slate-200/70 bg-slate-50/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: dot }} />
              {title}
            </div>
            <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200">
              {periodLengthDays(period)} dia(s)
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Início
              <input
                type="date"
                value={period.from}
                onChange={(event) => onChange({ from: event.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-[#2E8B57] focus:ring-2 focus:ring-[#2E8B57]/20"
              />
            </label>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Fim
              <input
                type="date"
                value={period.to}
                onChange={(event) => onChange({ to: event.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-[#2E8B57] focus:ring-2 focus:ring-[#2E8B57]/20"
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CardShell = ({ icon: Icon, title, subtitle, action, children, accent = C.pos.fg }) => (
  <div className="flex flex-col rounded-xl border border-slate-200/70 bg-white p-4 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color: accent }} />
        <div>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const TREND_METRICS = [
  { id: 'avg', label: 'Nota média' },
  { id: 'count', label: 'Quantidade' },
  { id: 'neg', label: 'Negativos' },
];

const ChartTooltip = ({ active, payload, metric }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const metricLabel = TREND_METRICS.find((item) => item.id === metric)?.label;
  const renderValue = (value) => {
    if (metric === 'avg') return value == null ? 'sem nota' : formatNumber(value, 2);
    if (metric === 'neg') return `${formatNumber(value)} negativo(s)`;
    return `${formatNumber(value)} feedback(s)`;
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur">
      <div className="mb-1 font-bold text-slate-700">Dia {row.idx} · {metricLabel}</div>
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: P2_COLOR }} />
        <span className="text-slate-500">Agora · {formatDateLabel(row.p2Date)}:</span>
        <strong className="text-slate-800">{renderValue(row.p2Value, row.p2Count)}</strong>
      </div>
      <div className="mt-0.5 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: P1_COLOR }} />
        <span className="text-slate-500">Antes · {formatDateLabel(row.p1Date)}:</span>
        <strong className="text-slate-800">{renderValue(row.p1Value, row.p1Count)}</strong>
      </div>
    </div>
  );
};

const TrendCard = ({ series1, series2, metric, onMetricChange }) => {
  const maxLen = Math.max(series1.length, series2.length);
  const valueOf = (day) => {
    if (!day) return null;
    if (metric === 'avg') return day.avg;
    if (metric === 'neg') return day.neg;
    return day.count;
  };
  const data = Array.from({ length: maxLen }, (_, index) => {
    const d1 = series1[index];
    const d2 = series2[index];
    return {
      idx: index + 1,
      p1Value: valueOf(d1),
      p2Value: valueOf(d2),
      p1Count: d1?.count ?? 0,
      p2Count: d2?.count ?? 0,
      p1Date: d1?.date ?? null,
      p2Date: d2?.date ?? null,
    };
  });

  const hasData = data.some((row) => row.p1Value != null || row.p2Value != null);
  const yDomain = metric === 'avg' ? [0, 5] : [0, 'auto'];

  return (
    <CardShell
      icon={LineChartIcon}
      title="Evolução dia a dia"
      subtitle="A linha verde é o período de agora; a cinza tracejada é o de antes. D1 é o 1º dia de cada período."
      accent={C.pos.fg}
      action={
        <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {TREND_METRICS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onMetricChange(item.id)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-bold transition ${
                metric === item.id ? 'bg-white text-[#1F5C3A] shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      }
    >
      {!hasData ? (
        <div className="flex h-[260px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/60 text-sm text-slate-500">
          Sem dados suficientes para o gráfico neste recorte.
        </div>
      ) : (
        <>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="trend-p2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={P2_COLOR} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={P2_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.07)" vertical={false} />
                <XAxis
                  dataKey="idx"
                  tick={{ fontSize: 11, fill: C.subtext }}
                  tickFormatter={(value) => `D${value}`}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(15,23,42,0.12)' }}
                />
                <YAxis
                  domain={yDomain}
                  tick={{ fontSize: 11, fill: C.subtext }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                  allowDecimals={metric === 'avg'}
                />
                <RechartsTooltip content={<ChartTooltip metric={metric} />} />
                <Area
                  type="monotone"
                  dataKey="p2Value"
                  stroke="transparent"
                  fill="url(#trend-p2)"
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="p1Value"
                  name="Antes"
                  stroke={P1_COLOR}
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="p2Value"
                  name="Agora"
                  stroke={P2_COLOR}
                  strokeWidth={2.6}
                  dot={false}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex items-center justify-center gap-4 text-[11px] font-semibold text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-5 rounded" style={{ backgroundColor: P2_COLOR }} />
              Agora
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-5 rounded" style={{ background: `repeating-linear-gradient(90deg, ${P1_COLOR} 0 5px, transparent 5px 9px)` }} />
              Antes
            </span>
          </div>
        </>
      )}
    </CardShell>
  );
};

const RatingDistributionCard = ({ dist1, dist2 }) => {
  const data = [5, 4, 3, 2, 1].map((star) => ({
    star: `${star}★`,
    p1: dist1[star] || 0,
    p2: dist2[star] || 0,
  }));
  const hasData = data.some((row) => row.p1 > 0 || row.p2 > 0);

  return (
    <CardShell
      icon={Star}
      title="Notas recebidas (estrelas)"
      subtitle="Quantas avaliações de cada nota você recebeu, comparando antes e agora."
      accent={C.neu.fg}
    >
      {!hasData ? (
        <div className="flex h-[240px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/60 text-sm text-slate-500">
          Sem notas registradas nos períodos.
        </div>
      ) : (
        <div className="h-[240px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.07)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: C.subtext }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="star" tick={{ fontSize: 12, fill: C.text, fontWeight: 700 }} tickLine={false} axisLine={false} width={34} />
              <RechartsTooltip
                cursor={{ fill: 'rgba(15,23,42,0.04)' }}
                contentStyle={{ borderRadius: 10, border: '1px solid rgba(15,23,42,0.1)', fontSize: 12 }}
                formatter={(value, key) => [formatNumber(value), key === 'p1' ? 'Antes' : 'Agora']}
              />
              <Bar dataKey="p1" name="Antes" fill={P1_COLOR} radius={[0, 4, 4, 0]} />
              <Bar dataKey="p2" name="Agora" fill={P2_COLOR} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </CardShell>
  );
};

const SentimentRow = ({ label, summary }) => {
  const total = Math.max(1, summary.total);
  const segments = [
    { key: 'pos', value: summary.positivos, color: C.pos.accent },
    { key: 'neu', value: summary.neutros, color: C.neu.accent },
    { key: 'neg', value: summary.negativos, color: C.neg.accent },
  ];
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
        <span>{label}</span>
        <span className="text-slate-400">{summary.total} feedback(s)</span>
      </div>
      <div className="flex h-3.5 overflow-hidden rounded-full bg-slate-100">
        {segments.map((segment) => (
          <div
            key={segment.key}
            title={`${formatNumber((segment.value / total) * 100, 0)}%`}
            style={{ width: `${(segment.value / total) * 100}%`, backgroundColor: segment.color }}
          />
        ))}
      </div>
    </div>
  );
};

const SentimentShiftCard = ({ p1, p2 }) => (
  <CardShell
    icon={Layers}
    title="Satisfação dos clientes"
    subtitle="Quanto dos clientes ficaram satisfeitos, neutros ou insatisfeitos."
    accent={C.pos.fg}
  >
    <div className="space-y-4">
      <SentimentRow label="Agora" summary={p2} />
      <SentimentRow label="Antes" summary={p1} />
    </div>
    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-semibold text-slate-500">
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: C.pos.accent }} />Positivos</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: C.neu.accent }} />Neutros</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: C.neg.accent }} />Negativos</span>
    </div>
  </CardShell>
);

const MoverPill = ({ tone, label, categoria, value }) => (
  <div
    className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
    style={{ backgroundColor: tone.bg, borderColor: tone.border }}
  >
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: tone.text }}>{label}</div>
      <div className="truncate text-sm font-bold text-slate-800">{categoria || 'Sem variação'}</div>
    </div>
    {categoria && (
      <span className="shrink-0 text-sm font-extrabold" style={{ color: tone.text }}>
        {value > 0 ? '+' : ''}{value}
      </span>
    )}
  </div>
);

const CategoryMoversCard = ({ rows }) => {
  const [sortKey, setSortKey] = useState('deltaNegativo');
  const [sortDir, setSortDir] = useState('desc');

  const worse = rows.find((row) => row.deltaNegativo > 0);
  const better = [...rows].reverse().find((row) => row.deltaNegativo < 0)
    || [...rows].sort((a, b) => b.deltaPositivo - a.deltaPositivo).find((row) => row.deltaPositivo > 0);

  const sorted = useMemo(() => {
    const factor = sortDir === 'desc' ? -1 : 1;
    const read = (row) => {
      if (sortKey === 'categoria') return row.categoria.toLowerCase();
      if (sortKey === 'deltaPositivo') return row.deltaPositivo;
      if (sortKey === 'p2neg') return row.p2.negativo;
      return row.deltaNegativo;
    };
    return [...rows].sort((a, b) => {
      const av = read(a);
      const bv = read(b);
      if (typeof av === 'string') return av.localeCompare(bv) * (sortDir === 'desc' ? -1 : 1);
      return (av - bv) * factor;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const headerButton = (key, label, alignRight = true) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 transition hover:text-slate-700 ${alignRight ? 'ml-auto' : ''}`}
    >
      {label}
      {sortKey === key && <span className="text-[9px]">{sortDir === 'desc' ? '▼' : '▲'}</span>}
    </button>
  );

  return (
    <CardShell
      icon={ListChecks}
      title="Assuntos mais comentados"
      subtitle="Os assuntos que seus clientes mais citaram. Toque em uma coluna para reordenar."
      accent={C.pos.fg}
    >
      {rows.length === 0 ? (
        <div className="px-2 py-8 text-center text-sm text-slate-500">
          Sem categorias classificadas nos períodos selecionados.
        </div>
      ) : (
        <>
          <div className="mb-4 grid gap-2 sm:grid-cols-2">
            <MoverPill
              tone={{ text: C.neg.fg, bg: C.neg.bg, border: 'rgba(212,109,99,0.25)' }}
              label="Onde mais piorou"
              categoria={worse?.categoria}
              value={worse?.deltaNegativo}
            />
            <MoverPill
              tone={{ text: C.pos.fg, bg: C.pos.bg, border: 'rgba(46,139,87,0.25)' }}
              label="Onde mais melhorou"
              categoria={better?.categoria}
              value={better ? (better.deltaNegativo < 0 ? better.deltaNegativo : better.deltaPositivo) : undefined}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70">
                  <th className="px-3 py-2 text-left">{headerButton('categoria', 'Assunto', false)}</th>
                  <th className="px-3 py-2 text-right">{headerButton('p2neg', 'Reclamações agora')}</th>
                  <th className="px-3 py-2 text-right">{headerButton('deltaNegativo', 'Mudança nas reclamações')}</th>
                  <th className="px-3 py-2 text-right">{headerButton('deltaPositivo', 'Mudança nos elogios')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => (
                  <tr key={row.categoria} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{row.categoria}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{row.p2.negativo}</td>
                    <td className="px-3 py-2.5 text-right">
                      <DeltaBadge value={row.deltaNegativo} inverse />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <DeltaBadge value={row.deltaPositivo} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </CardShell>
  );
};

const InsightList = ({ insights }) => (
  <CardShell
    icon={Sparkles}
    title="Resumo automático"
    subtitle="Em frases simples, o que mudou de antes para agora."
    accent={C.pos.fg}
  >
    <ul className="space-y-2">
      {insights.map((item, index) => (
        <li
          key={`${item.text}-${index}`}
          className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm"
          style={{
            color: item.tone === 'neg' ? C.neg.fg : item.tone === 'pos' ? C.pos.fg : C.text,
            backgroundColor: item.tone === 'neg' ? C.neg.bg : item.tone === 'pos' ? C.pos.bg : '#F8FAFC',
            borderColor: item.tone === 'neg' ? 'rgba(212,109,99,0.2)' : item.tone === 'pos' ? 'rgba(46,139,87,0.2)' : 'rgba(15,23,42,0.06)',
          }}
        >
          <span className="mt-0.5">
            {item.tone === 'neg' ? <TrendingDown size={15} /> : item.tone === 'pos' ? <TrendingUp size={15} /> : <Minus size={15} />}
          </span>
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  </CardShell>
);

const CriticalFeedbacks = ({ feedbacks }) => {
  const critical = feedbacks.filter((item) => Number(item.nota || 0) > 0 && Number(item.nota || 0) <= 2).slice(0, 4);

  return (
    <CardShell
      icon={AlertTriangle}
      title="Avaliações que precisam de atenção"
      subtitle="Notas 1 e 2 do período de agora. Vale a pena responder a esses clientes."
      accent={C.neg.fg}
    >
      {critical.length === 0 ? (
        <p className="rounded-lg bg-[#EAF7EE] px-3 py-2 text-sm font-semibold text-[#1F5C3A]">
          Nenhum feedback crítico no período. 🎉
        </p>
      ) : (
        <ul className="space-y-2">
          {critical.map((item) => (
            <li key={item.id} className="rounded-lg border border-[#F7D8D2] bg-[#FCEAE7]/40 px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-bold uppercase tracking-wider text-[#7A2F2A]">Nota {item.nota}</span>
                <span className="text-[11px] text-slate-500">{item.cliente_nome || item.cliente_telefone || 'Cliente'}</span>
              </div>
              <p className="mt-1 line-clamp-3 text-sm text-slate-700">{item.comentario || 'Sem comentário.'}</p>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
};

const FeedbackPeriodComparison = () => {
  const presets = useMemo(buildPresets, []);
  const initialPeriods = useMemo(buildInitialPeriods, []);
  const [period1, setPeriod1] = useState(initialPeriods.period1);
  const [period2, setPeriod2] = useState(initialPeriods.period2);
  const [autoCompare, setAutoCompare] = useState(true);
  const [activePresetId, setActivePresetId] = useState('30d');
  const [trendMetric, setTrendMetric] = useState('avg');
  const [data, setData] = useState({ period1: null, period2: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const { fonts, colors } = feedbackHeroTokens;

  useEffect(() => {
    let active = true;

    const fetchPeriod = async (period) => {
      const params = { from: period.from, to: period.to };
      const [summaryResponse, pointsResponse, feedbacksResponse] = await Promise.all([
        api.get('/feedback/feedbacks/resumo', { params }),
        api.get('/feedback/feedbacks/pontos', { params }),
        api.get('/feedback/feedbacks', { params: { ...params, ordem: 'desc' } }),
      ]);

      return {
        summary: normalizeSummary(summaryResponse.data || {}),
        points: pointsResponse.data?.data || [],
        feedbacks: readFeedbackList(feedbacksResponse.data),
      };
    };

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [nextPeriod1, nextPeriod2] = await Promise.all([
          fetchPeriod(period1),
          fetchPeriod(period2),
        ]);
        if (!active) return;
        setData({ period1: nextPeriod1, period2: nextPeriod2 });
      } catch (err) {
        console.error('Erro ao buscar comparativo de feedbacks:', err);
        if (active) setError('Não foi possível carregar a análise dos períodos.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [period1, period2, refreshKey]);

  const handlePreset = (preset) => {
    setActivePresetId(preset.id);
    setPeriod2(preset.period);
    if (autoCompare) setPeriod1(previousPeriodOf(preset.period));
  };

  const handleChangePeriod2 = (patch) => {
    setActivePresetId(null);
    setPeriod2((prev) => {
      const next = { ...prev, ...patch };
      if (autoCompare) setPeriod1(previousPeriodOf(next));
      return next;
    });
  };

  const handleChangePeriod1 = (patch) => {
    setActivePresetId(null);
    setAutoCompare(false);
    setPeriod1((prev) => ({ ...prev, ...patch }));
  };

  const handleToggleAuto = (checked) => {
    setAutoCompare(checked);
    if (checked) setPeriod1(previousPeriodOf(period2));
  };

  const comparison = useMemo(() => {
    const p1 = data.period1?.summary || normalizeSummary();
    const p2 = data.period2?.summary || normalizeSummary();
    const feedbacks1 = data.period1?.feedbacks || [];
    const feedbacks2 = data.period2?.feedbacks || [];

    const series1 = buildDailySeries(feedbacks1, period1);
    const series2 = buildDailySeries(feedbacks2, period2);
    const dist1 = buildRatingDistribution(feedbacks1);
    const dist2 = buildRatingDistribution(feedbacks2);

    const p1Points = buildPointMap(data.period1?.points || []);
    const p2Points = buildPointMap(data.period2?.points || []);
    const categories = [...new Set([...p1Points.keys(), ...p2Points.keys()])];
    const categoryRows = categories
      .map((categoria) => {
        const empty = { categoria, positivo: 0, negativo: 0 };
        const a = p1Points.get(categoria) || empty;
        const b = p2Points.get(categoria) || empty;
        return {
          categoria,
          p1: a,
          p2: b,
          deltaPositivo: b.positivo - a.positivo,
          deltaNegativo: b.negativo - a.negativo,
        };
      })
      .sort((a, b) => Math.abs(b.deltaNegativo) - Math.abs(a.deltaNegativo));

    const avgDelta = (p2.media ?? 0) - (p1.media ?? 0);
    const volumeDelta = p2.total - p1.total;
    const positiveRateDelta = p2.taxaPositiva - p1.taxaPositiva;
    const negativeRateDelta = p2.taxaNegativa - p1.taxaNegativa;
    const pendingDelta = p2.pendencias - p1.pendencias;
    const topNegativeGrowth = categoryRows.find((row) => row.deltaNegativo > 0);
    const topNegativeDrop = categoryRows.find((row) => row.deltaNegativo < 0);

    const insights = [];
    if (p1.total === 0 && p2.total === 0) {
      insights.push({ tone: 'neutral', text: 'Nenhuma avaliação encontrada nos períodos escolhidos.' });
    } else {
      insights.push({
        tone: avgDelta > 0.01 ? 'pos' : avgDelta < -0.01 ? 'neg' : 'neutral',
        text:
          Math.abs(avgDelta) < 0.01
            ? 'A nota média continua quase igual à de antes.'
            : `A nota média ${avgDelta > 0 ? 'subiu' : 'caiu'} de ${formatNumber(p1.media ?? 0, 2)} para ${formatNumber(p2.media ?? 0, 2)} estrelas.`,
      });
      insights.push({
        tone: volumeDelta > 0 ? 'pos' : volumeDelta < 0 ? 'neg' : 'neutral',
        text:
          volumeDelta === 0
            ? 'Você recebeu a mesma quantidade de avaliações que antes.'
            : `Você recebeu ${Math.abs(volumeDelta)} ${Math.abs(volumeDelta) === 1 ? 'avaliação' : 'avaliações'} ${volumeDelta > 0 ? 'a mais' : 'a menos'} que antes (de ${p1.total} para ${p2.total}).`,
      });
      insights.push({
        tone: negativeRateDelta < -0.01 ? 'pos' : negativeRateDelta > 0.01 ? 'neg' : 'neutral',
        text:
          Math.abs(negativeRateDelta) < 0.01
            ? 'A fatia de avaliações negativas continua parecida com a de antes.'
            : `As avaliações negativas ${negativeRateDelta > 0 ? 'aumentaram' : 'diminuíram'}: agora são ${formatPercent(p2.taxaNegativa)} do total.`,
      });
      if (Math.abs(positiveRateDelta) >= 0.01) {
        insights.push({
          tone: positiveRateDelta > 0 ? 'pos' : 'neg',
          text: `As avaliações positivas ${positiveRateDelta > 0 ? 'aumentaram' : 'diminuíram'}: agora são ${formatPercent(p2.taxaPositiva)} do total.`,
        });
      }
      if (pendingDelta !== 0) {
        insights.push({
          tone: pendingDelta > 0 ? 'neg' : 'pos',
          text: `Há ${Math.abs(pendingDelta)} ${Math.abs(pendingDelta) === 1 ? 'avaliação negativa' : 'avaliações negativas'} sem resposta ${pendingDelta > 0 ? 'a mais' : 'a menos'} que antes.`,
        });
      }
      if (topNegativeGrowth) {
        insights.push({
          tone: 'neg',
          text: `O assunto que mais gerou reclamações novas: "${topNegativeGrowth.categoria}" (+${topNegativeGrowth.deltaNegativo}).`,
        });
      } else if (topNegativeDrop) {
        insights.push({
          tone: 'pos',
          text: `O assunto que mais melhorou nas reclamações: "${topNegativeDrop.categoria}" (${topNegativeDrop.deltaNegativo}).`,
        });
      }
    }

    return {
      p1,
      p2,
      series1,
      series2,
      dist1,
      dist2,
      categoryRows,
      insights,
      deltas: { avgDelta, volumeDelta, positiveRateDelta, negativeRateDelta, pendingDelta },
    };
  }, [data, period1, period2]);

  return (
    <div className="feedback-hero-root px-2 pb-12 sm:px-6 sm:pb-16">
      <div
        className="mx-auto max-w-[1280px] space-y-5 rounded-2xl border p-3 sm:rounded-3xl sm:p-5 md:p-6"
        style={{
          borderColor: colors.softBorder,
          background:
            'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
        }}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF7EE] text-[#1F5C3A]">
              <LineChartIcon size={22} />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" style={{ fontFamily: fonts.display }}>
                Evolução dos feedbacks
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">
                Veja o que melhorou ou piorou nas suas avaliações, comparando um período com o anterior.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs font-semibold text-slate-600">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-white/70 px-2 py-1 ring-1 ring-slate-200">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: P1_COLOR }} />
                  Antes: {formatRangeLabel(period1)}
                </span>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-white/70 px-2 py-1 ring-1 ring-slate-200">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: P2_COLOR }} />
                  Agora: {formatRangeLabel(period2)}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRefreshKey((value) => value + 1)}
            disabled={loading}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#2E8B57] px-4 text-sm font-bold text-white shadow-sm transition hover:bg-[#1F5C3A] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>

        <PeriodControlBar
          presets={presets}
          activePresetId={activePresetId}
          onPreset={handlePreset}
          autoCompare={autoCompare}
          onToggleAuto={handleToggleAuto}
          period1={period1}
          period2={period2}
          onChangePeriod1={handleChangePeriod1}
          onChangePeriod2={handleChangePeriod2}
        />

        {error && (
          <div className="rounded-xl border border-[#D46D63]/25 bg-[#FCEAE7] px-4 py-3 text-sm font-semibold text-[#7A2F2A]">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-slate-200/70 bg-white p-6 text-sm text-slate-500">
            Carregando análise dos períodos...
          </div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <KpiCard
                icon={MessageSquareText}
                label="Feedbacks"
                value={comparison.p2.total}
                previous={comparison.p1.total}
                diff={comparison.deltas.volumeDelta}
                hint="Total de avaliações recebidas."
              />
              <KpiCard
                icon={Star}
                label="Nota média"
                value={comparison.p2.media ?? 0}
                previous={comparison.p1.media ?? 0}
                diff={comparison.deltas.avgDelta}
                decimals={2}
                hint="Média das estrelas (de 1 a 5)."
              />
              <KpiCard
                icon={TrendingUp}
                label="% Positivos"
                value={comparison.p2.taxaPositiva}
                previous={comparison.p1.taxaPositiva}
                diff={comparison.deltas.positiveRateDelta}
                suffix="%"
                decimals={1}
                hint="Avaliações de 4 e 5 estrelas."
              />
              <KpiCard
                icon={TrendingDown}
                label="% Negativos"
                value={comparison.p2.taxaNegativa}
                previous={comparison.p1.taxaNegativa}
                diff={comparison.deltas.negativeRateDelta}
                suffix="%"
                inverse
                decimals={1}
                hint="Avaliações de 1 e 2 estrelas."
              />
              <KpiCard
                icon={AlertTriangle}
                label="Pendências"
                value={comparison.p2.pendencias}
                previous={comparison.p1.pendencias}
                diff={comparison.deltas.pendingDelta}
                inverse
                hint="Avaliações negativas ainda sem resposta."
              />
            </div>

            <TrendCard
              series1={comparison.series1}
              series2={comparison.series2}
              metric={trendMetric}
              onMetricChange={setTrendMetric}
            />

            <div className="grid gap-3 lg:grid-cols-2">
              <RatingDistributionCard dist1={comparison.dist1} dist2={comparison.dist2} />
              <SentimentShiftCard p1={comparison.p1} p2={comparison.p2} />
            </div>

            <div className="grid gap-3 xl:grid-cols-[1.05fr_0.95fr]">
              <InsightList insights={comparison.insights} />
              <CriticalFeedbacks feedbacks={data.period2?.feedbacks || []} />
            </div>

            <CategoryMoversCard rows={comparison.categoryRows} />
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackPeriodComparison;
